// src/features/dashboard/DashboardSkeleton.tsx
// Layout mirrors DashboardPage 1:1 — same grid, same classes, same hierarchy
//   Grid:    [kpiRowArea (span 3 cols)] → 5 KPI cards
//                 ↓
//   Row 1:   [evolucao | metas | modelos]
//   Row 2:   [top-margem | clientes | linha]
//   Below:   [vendas-por-mes] (full-width)
import React from 'react';
import { SkBox, SkText } from '../../components/SkeletonLoader/SkeletonLoader';
import styles from './DashboardSkeleton.module.css';

/* ---------- Shared sub-components ---------- */

/** KPI card — 10px uppercase title, 28px value, 14px label (matches KPICard) */
const KpiCardSk: React.FC = () => (
  <div className={styles.kpiCard}>
    <SkText width="50%" height={10} />
    <SkText width="45%" height={28} />
    <SkText width="35%" height={14} />
  </div>
);

/* ---------- Main component ---------- */

const DashboardSkeleton: React.FC = () => (
  <div className={styles.skeletonWrapper}>

    {/*
      ──────────────────────────────────────────────
      MAIN CONTENT GRID:  1fr 1fr 1fr
      ──────────────────────────────────────────────
      KPI Row spans all 3 columns via grid-column: 1 / -1
    */}
    <div className={styles.mainContentGrid}>

      {/* ═══ KPI ROW (5 equal cards, full width) ═══ */}
      <div className={styles.kpiRowArea}>
        <KpiCardSk />
        <KpiCardSk />
        <KpiCardSk />
        <KpiCardSk />
        <KpiCardSk />
      </div>

      {/*
        ═══ ROW 1 — Col 1: Evolução de Vendas ═══
        Uses chartWidget + chartWidget_desempenho style
      */}
      <div className={styles.widget}>
        <p className={styles.widgetTitle}>Evolução de Vendas</p>
        <div className={styles.chartArea}>
          <SkBox width="100%" height={220} />
        </div>
      </div>

      {/*
        ═══ ROW 1 — Col 2: Metas ═══
        ModernGoalWidget: gauge + 5 indicadores + barras de progresso
      */}
      <div className={styles.widget}>
        <p className={styles.widgetTitle}>Meta</p>
        <div className={styles.metasContent}>
          {/* Linha principal: gauge + indicadores */}
          <div className={styles.metasRow}>
            {/* Semi-circle gauge */}
            <div className={styles.gaugeColSk}>
              <div className={styles.gaugeSemi}>
                <div className={styles.gaugeSemiInner} />
                <div className={styles.gaugeCenterText}>
                  <SkText width="42%" height={30} />
                </div>
              </div>
              <SkText width={70} height={13} />
              <SkText width={80} height={12} />
            </div>
            {/* 5 indicadores */}
            <div className={styles.indColSk}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className={styles.indRowSk}>
                  <SkText width={18} height={18} />
                  <div>
                    <SkText width={90} height={10} />
                    <SkText width={70} height={16} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Barras de progresso */}
          <div className={styles.metasBars}>
            <div className={styles.metasBarItem}>
              <SkText width={100} height={10} />
              <SkText width={120} height={12} />
              <SkBox width="100%" height={8} />
            </div>
            <div className={styles.metasBarItem}>
              <SkText width={100} height={10} />
              <SkText width={120} height={12} />
              <SkBox width="100%" height={8} />
            </div>
          </div>
        </div>
      </div>

      {/*
        ═══ ROW 1 — Col 3: Principais Modelos ═══
        chartWidget_produtos: header with inline title + responsive tabs area
      */}
      <div className={styles.widget}>
        <div className={styles.widgetHeader}>
          <p className={styles.widgetTitleInline}>Principais Modelos</p>
          {/* Fake tabs */}
          <div className={styles.tabsContainer}>
            <div className={styles.tabButton}>
              <SkText width={40} height={9} />
            </div>
            <div className={styles.tabButton}>
              <SkText width={40} height={9} />
            </div>
          </div>
        </div>
        {/* Horizontal bar chart simulation */}
        <div className={styles.barChartArea}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={styles.barRow}>
              <div className={styles.barLabel}>
                <SkText width={80} height={10} />
              </div>
              <div
                className={styles.barTrack}
                style={{ width: `${50 + Math.random() * 40}%` }}
              />
            </div>
          ))}
        </div>
      </div>

      {/*
        ═══ ROW 2 — Col 1: Maiores Clientes Inativos (90+ dias) ═══
        Horizontal bar chart with inactive clients
      */}
      <div className={styles.widget} style={{ minHeight: 300 }}>
        <p className={styles.widgetTitle}>Maiores Clientes Inativos (90+ dias)</p>
        <div className={styles.chartArea}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={styles.hBarRow}>
              <SkText width="30%" height={10} />
              <div style={{ flex: 1, position: 'relative' }}>
                <SkBox
                  width={`${40 + Math.random() * 50}%`}
                  height={14}
                  style={{ borderRadius: '0 4px 4px 0' }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/*
        ═══ ROW 2 — Col 2: Principais Clientes ═══
        Horizontal bar chart (like TopClientsBarChart)
      */}
      <div className={styles.widget} style={{ minHeight: 300 }}>
        <p className={styles.widgetTitle}>Principais Clientes</p>
        <div className={styles.chartArea}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={styles.clientBarRow}>
              <SkText width="55%" height={10} />
              <div
                className={styles.clientBarTrack}
                style={{ width: `${30 + Math.random() * 60}%` }}
              />
            </div>
          ))}
        </div>
      </div>

      {/*
        ═══ ROW 2 — Col 3: Participação por Linha ═══
        Donut chart + color legend
      */}
      <div className={styles.widget} style={{ minHeight: 320 }}>
        <p className={styles.widgetTitle}>Participação por Linha</p>
        <div className={styles.donutContent}>
          {/* Donut */}
          <div className={styles.donutCircle}>
            <div className={styles.donutHole} />
          </div>
          {/* Legend */}
          <div className={styles.legendCol}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={styles.legendRow}>
                <div className={styles.legendDot} />
                <SkText width="70%" height={10} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/*
      ═══ FULL-WIDTH: Vendas por Mês (below the grid) ═══
      Matches the chartWidget below mainContentGrid
    */}
    <div className={styles.widgetFull}>
      <p className={styles.widgetTitleCentered}>Vendas por Mês</p>
      <SkBox width="100%" height={220} className={styles.chartArea} />
    </div>

  </div>
);

export default DashboardSkeleton;
